import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Icon from './Icon';

const FullPage = ({ children }) => (
  <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: 'var(--bg-canvas)', padding: 'var(--s6)' }}>
    {children}
  </div>
);

const Pending = () => (
  <FullPage>
    <div className="card" style={{ width: '100%', maxWidth: 420, textAlign: 'center', padding: 'var(--s8)' }}>
      <div className="empty-icon" style={{ margin: '0 auto var(--s4)' }}>
        <Icon name="clock" size={26} />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>Awaiting approval</h2>
      <p style={{ color: 'var(--tx2)', fontSize: 14, lineHeight: 1.6 }}>
        Your account is pending admin review. An admin will be notified and you'll receive an email when access is granted.
      </p>
    </div>
  </FullPage>
);

const Rejected = () => (
  <FullPage>
    <div className="card" style={{ width: '100%', maxWidth: 420, textAlign: 'center', padding: 'var(--s8)', borderColor: 'var(--err)' }}>
      <div className="empty-icon" style={{ margin: '0 auto var(--s4)', background: 'var(--err-bg)', color: 'var(--err)' }}>
        <Icon name="x" size={26} />
      </div>
      <h2 style={{ fontSize: 22, fontWeight: 800, color: 'var(--tx1)', marginBottom: 'var(--s2)' }}>Access denied</h2>
      <p style={{ color: 'var(--tx2)', fontSize: 14, lineHeight: 1.6 }}>
        Your account has been rejected by an admin. Contact security@coynitt.ca if this is in error.
      </p>
    </div>
  </FullPage>
);

const Loading = () => (
  <FullPage>
    <div className="text-tx3 text-sm">Loading…</div>
  </FullPage>
);

// Belt-and-braces fallback: AuthContext has its own 8s boot timeout that
// flips session→null on stall, but if we ever land in the (session truthy,
// profile null) state for longer than 5s — e.g. profile fetch silently
// returned null for a non-auth reason — bounce to /signin so users aren't
// stranded on the spinner.
const PROFILE_TIMEOUT_MS = 5000;

const LoadingWithTimeout = ({ reason }) => {
  const [timedOut, setTimedOut] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setTimedOut(true), PROFILE_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);
  if (timedOut) return <Navigate to={`/signin?reason=${reason}`} replace />;
  return <Loading />;
};

const ProtectedRoute = ({ children }) => {
  const { loading, session, profile, mfaRequired } = useAuth();
  const location = useLocation();

  if (loading) return <Loading />;
  if (!session) return <Navigate to="/signin" state={{ from: location }} replace />;
  // Signed in with only a password but a verified authenticator exists — the
  // session is aal1 and must be elevated to aal2. Bounce to /signin, which
  // renders the TOTP challenge (it sees session + mfaRequired). Prevents any
  // dashboard route from rendering at the lower assurance level.
  if (mfaRequired) return <Navigate to="/signin?reason=mfa_required" state={{ from: location }} replace />;
  // Session valid but profile fetch hasn't returned (or returned null for a
  // non-auth reason). Show the loader briefly, then redirect — never spin
  // forever.
  if (!profile) return <LoadingWithTimeout reason="profile_load_failed" />;

  if (profile.status === 'pending')  return <Pending />;
  if (profile.status === 'rejected') return <Rejected />;
  return children;
};

export default ProtectedRoute;
